import { baseApi } from '@/shared/api/baseApi';
import type { CategoryDto, CategoryPriceHistoryEntry, CreateCategoryRequest, UpdateCategoryRequest } from '../model/types';

const categoryApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    listCategories: builder.query<CategoryDto[], { includeArchived?: boolean } | void>({
      query: (params) => ({ url: 'categories', params: params ?? undefined }),
      providesTags: ['Category'],
    }),
    searchCategories: builder.query<
      CategoryDto[],
      { q?: string; limit?: number; includeArchived?: boolean; includeGroups?: boolean; parentId?: string } | void
    >({
      query: (params) => ({ url: 'categories/search', params: params ?? undefined }),
      providesTags: ['Category'],
    }),
    createCategory: builder.mutation<CategoryDto, CreateCategoryRequest>({
      query: (body) => ({ url: 'categories', method: 'POST', body }),
      invalidatesTags: ['Category'],
    }),
    updateCategory: builder.mutation<CategoryDto, { id: string; body: UpdateCategoryRequest }>({
      query: ({ id, body }) => ({ url: `categories/${id}`, method: 'PATCH', body }),
      invalidatesTags: ['Category'],
    }),
    setWholesalePrice: builder.mutation<CategoryDto, { id: string; price: string }>({
      query: ({ id, price }) => ({ url: `categories/${id}/wholesale-price`, method: 'PATCH', body: { price } }),
      invalidatesTags: ['Category'],
    }),
    archiveCategory: builder.mutation<CategoryDto, string>({
      query: (id) => ({ url: `categories/${id}/archive`, method: 'POST' }),
      invalidatesTags: ['Category'],
    }),
    unarchiveCategory: builder.mutation<CategoryDto, string>({
      query: (id) => ({ url: `categories/${id}/unarchive`, method: 'POST' }),
      invalidatesTags: ['Category'],
    }),
    categoryPriceHistory: builder.query<CategoryPriceHistoryEntry[], string>({
      query: (id) => `categories/${id}/price-history`,
      providesTags: (_r, _e, id) => [{ type: 'Category', id }],
    }),
    uploadCategoryImage: builder.mutation<CategoryDto, { id: string; file: File }>({
      query: ({ id, file }) => {
        const formData = new FormData();
        formData.append('file', file);
        return { url: `categories/${id}/image`, method: 'POST', body: formData };
      },
      invalidatesTags: ['Category'],
    }),
    removeCategoryImage: builder.mutation<CategoryDto, string>({
      query: (id) => ({ url: `categories/${id}/image`, method: 'DELETE' }),
      invalidatesTags: ['Category'],
    }),
  }),
});

export const {
  useListCategoriesQuery,
  useSearchCategoriesQuery,
  useCreateCategoryMutation,
  useUpdateCategoryMutation,
  useSetWholesalePriceMutation,
  useArchiveCategoryMutation,
  useUnarchiveCategoryMutation,
  useCategoryPriceHistoryQuery,
  useUploadCategoryImageMutation,
  useRemoveCategoryImageMutation,
} = categoryApi;

export { categoryApi };
